import { useEffect, useRef } from "react";
import { clearCelebrate, useCelebrate, type Celebrate } from "../../lib/celebrate";
import { playPurchase, playStamp, playThrough } from "../../lib/sound";
import { NobFace } from "./mascot";

/** How long the plate stands before it gets out of the way on its own. */
const HOLD_MS = 2800;

/**
 * The one overlay in this product that is pleased about something.
 *
 * It dismisses itself. That is the whole reason it is allowed to exist: a
 * celebration with an OK button is a modal, and a modal is an interruption
 * somebody has to pay for. This one holds for under three seconds, takes a
 * click or an Escape at any point, and leaves nothing behind.
 *
 * The sound is fired once per event id — a re-render must not knock twice —
 * and is silent under reduced motion, which `lib/sound.ts` enforces at source.
 */
export function CelebrateOverlay() {
  const event = useCelebrate();
  const played = useRef<string | null>(null);

  useEffect(() => {
    if (!event) return;
    if (played.current !== event.id) {
      played.current = event.id;
      soundFor(event);
    }
    const timer = window.setTimeout(clearCelebrate, HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [event]);

  if (!event) return null;

  return (
    <div className="nob-scrim fixed inset-0 z-[60] grid place-items-center bg-ink/45 p-5">
      <button
        type="button"
        aria-label="Dismiss"
        tabIndex={-1}
        onClick={clearCelebrate}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="status"
        aria-live="polite"
        className="relative z-10 w-full max-w-[20.5rem] rounded-xl border border-border bg-bg
                   px-6 pt-6 pb-7 text-center text-text shadow-2xl"
      >
        <div className="flex justify-center">
          <NobFace mood="pleased" size="desk" />
        </div>
        <p
          aria-hidden
          className="mt-4 font-mono text-[11px] uppercase tracking-[0.28em] text-accent-ink-strong"
        >
          {event.stamp}
        </p>
        <h2 className="mt-3 text-lg font-semibold tracking-tight">{event.title}</h2>
        <p className="mt-2 text-sm leading-snug text-muted">{event.body}</p>
      </div>
    </div>
  );
}

function soundFor(event: Celebrate): void {
  if (event.kind === "through") return playThrough();
  if (event.kind === "plan") return playPurchase();
  playStamp();
}
